'use client';

import { API_ROUTES, useApiCacheControl } from '@shared/api-utils';
import { useSession } from '@shared/auth';
import { useLongPoll } from '@shared/hooks';
import type { Currency } from '@shared/utils';
import { useEffect, useState } from 'react';
import { useWalletBalanceHold } from './useWalletBalanceHold';

type WalletFrontofficeBalanceMoney = { value: number; currency: Currency };

type WalletFrontofficeBalanceResponse = {
  transactionId: number;
  withdrawableBalance: WalletFrontofficeBalanceMoney;
  lockedBalance: WalletFrontofficeBalanceMoney;
  totalBalance: WalletFrontofficeBalanceMoney;
  responseMsg: string;
};

type UseWalletFrontofficeBalanceResult = {
  totalBalance: number | undefined;
  withdrawableBalance: number | undefined;
  lockedBalance: number | undefined;
  isLoading: boolean;
};

/**
 * Polls the wallet balance endpoint and returns balances in pence,
 * consistent with the GET_TRANSACTIONS API and the useCurrencyFormatter convention.
 *
 * Falls back to undefined for each balance when the poll has not yet succeeded,
 * allowing callsites to fall back to GET_TRANSACTIONS values.
 */
function useWalletBalance(enabled = true): UseWalletFrontofficeBalanceResult {
  const [transactionId, setTransactionId] = useState(0);
  const { session } = useSession();
  const playerId = session.profile?.id;
  const countryCode = session.profile?.country;
  const { isHeld } = useWalletBalanceHold();
  const { cancelQuery } = useApiCacheControl();

  // Disabling the poll leaves a request already in flight. Cancel it so that response cannot replace the held figure.
  useEffect(() => {
    if (!isHeld || !playerId || !countryCode) {
      return;
    }

    cancelQuery(API_ROUTES.MY_ACCOUNT.WALLET.GET_BALANCE, {
      transactionId,
      countryCode,
    });
  }, [cancelQuery, countryCode, isHeld, playerId, transactionId]);

  const { data, isLoading } = useLongPoll<WalletFrontofficeBalanceResponse>(API_ROUTES.MY_ACCOUNT.WALLET.GET_BALANCE, {
    enabled: !!playerId && enabled && !!countryCode && !isHeld,
    credentials: true,
    intervalMs: 3000,
    params: {
      transactionId,
      countryCode: countryCode as string,
    },
    queryOptions: { staleTime: 0, placeholderData: (previousData: WalletFrontofficeBalanceResponse | undefined) => previousData },
  });

  useEffect(() => {
    if (data?.transactionId === undefined) {
      return;
    }
    const nextId = data.transactionId;
    queueMicrotask(() => {
      setTransactionId(nextId);
    });
  }, [data?.transactionId]);

  return {
    totalBalance: data?.totalBalance?.value !== undefined ? Math.round(Number(data.totalBalance.value) * 100) : undefined,
    withdrawableBalance: data?.withdrawableBalance?.value !== undefined ? Math.round(Number(data.withdrawableBalance.value) * 100) : undefined,
    lockedBalance: data?.lockedBalance?.value !== undefined ? Math.round(Number(data.lockedBalance.value) * 100) : undefined,
    isLoading,
  };
}

export { useWalletBalance };
export type { WalletFrontofficeBalanceMoney, WalletFrontofficeBalanceResponse };
